import { Grid, Typography } from '@mui/material';
import colors from 'styles/colors';
import { Fragment } from 'react';
import millisToHours from 'utils/millisToHours';
import { mediumWithoutSecondsFormatter } from 'utils/formatDateTime';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useCompetitionStore from 'store/competitionStore';

const styles = {
  rowStyles: {
    px: 1,
    display: 'flex',
    alignItems: 'center',
  },
  tableTitle: {
    color: colors.text.primary,
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
  },
  titleTooltip: {
    color: colors.text.secondary,
    fontSize: '16px',
    paddingLeft: '5px',
    paddingTop: '2px',
  },
};

const SubmissionList = ({ submissions, grid, points }) => {
  const isLeaderboardVisible = useCompetitionStore((state) => state.competition.details.isLeaderboardVisible);

  // Adjust grid sizes based on whether the time penalty column is visible
  const answerColumnSize = 5;
  const scoreColumnSize = isLeaderboardVisible ? 2 : 3;
  const submittedColumnSize = isLeaderboardVisible ? 3 : 4;

  return (
    <Grid container columns={grid}>
      <Grid
        container
        columns={grid}
        sx={{
          width: 'calc(100% - 5px)',
        }}
      >
        <Grid item xs={answerColumnSize} sx={styles.rowStyles}>
          <Typography
            gutterBottom
            sx={{
              ...styles.tableTitle, width: '25px', minWidth: '25px', marginRight: '16px',
            }}
          >
            No.
          </Typography>
          <Typography gutterBottom sx={styles.tableTitle}>
            Submitted answer
          </Typography>
        </Grid>
        <Grid item xs={scoreColumnSize} sx={styles.rowStyles}>
          <Typography gutterBottom sx={styles.tableTitle}>
            Score / Max Score
            <Tooltip
              arrow
              sx={{ backgroundColor: colors.background.primary }}
              title="Points for this problem"
              placement="top"
            >
              <InfoOutlinedIcon
                sx={styles.titleTooltip}
              />
            </Tooltip>
          </Typography>
        </Grid>
        {isLeaderboardVisible && (
          <Grid item xs={2} sx={styles.rowStyles}>
            <Typography gutterBottom sx={styles.tableTitle}>
              Time penalty
              <Tooltip
                arrow
                sx={{ backgroundColor: colors.background.primary }}
                title="Time it took to solve this problem. Wrong submissions do not count."
                placement="top"
              >
                <InfoOutlinedIcon
                  sx={styles.titleTooltip}
                />
              </Tooltip>
            </Typography>
          </Grid>
        )}
        <Grid item xs={submittedColumnSize} sx={styles.rowStyles}>
          <Typography gutterBottom sx={styles.tableTitle}>
            Submitted
          </Typography>
        </Grid>
      </Grid>

      <Grid
        container
        columns={grid}
        sx={{
          height: '80px',
          overflowY: 'auto',
          scrollbarGutter: 'stable',
          marginBottom: '16px',
        }}
      >
        {submissions.length ? (
          submissions.map((submission, index) => {
            const key = submission.submissionId || `${submission.timePenalty}-${index}`;
            return (
              <Fragment key={key}>
                <Grid item xs={answerColumnSize} sx={styles.rowStyles} key={`answer-${key}`}>
                  <Typography
                    gutterBottom
                    sx={{
                      color: colors.text.secondary,
                      width: '25px',
                      minWidth: '25px',
                      marginRight: '16px',
                      fontFamily: 'monospace',
                    }}
                  >
                    {index + 1}
                  </Typography>
                  <Typography
                    gutterBottom
                    sx={{ color: colors.text.secondary, overflowX: 'auto', fontSize: '14px' }}
                    className="monospace"
                  >
                    {submission.submissionAnswer}
                  </Typography>
                  {submission.warningMessage ? (
                    <Tooltip
                      arrow
                      sx={{ backgroundColor: colors.background.primary }}
                      title={submission.warningMessage ?? ''}
                      placement="right"
                    >
                      <InfoOutlinedIcon
                        sx={{
                          color: colors.text.secondary,
                          fontSize: '16px',
                          paddingLeft: '5px',
                          marginBottom: '0.35em',
                        }}
                      />
                    </Tooltip>
                  ) : null}
                </Grid>
                <Grid item xs={scoreColumnSize} sx={styles.rowStyles} key={`score-${key}`}>
                  <Typography className="monospace" gutterBottom sx={{ color: colors.success.light }}>
                    {submission.problemPoints}
                    /
                    {points}
                  </Typography>
                </Grid>
                {isLeaderboardVisible && (
                  <Grid item xs={2} sx={styles.rowStyles} key={`penalty-${key}`}>
                    <Typography className="monospace" gutterBottom sx={{ color: colors.error.light }}>
                      {submission?.timePenalty ? millisToHours((submission.timePenalty * 1000)) : '\u2014'}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={submittedColumnSize} sx={styles.rowStyles} key={`created-${key}`}>
                  <Typography gutterBottom sx={{ color: colors.text.primary, fontSize: '15px' }}>
                    {mediumWithoutSecondsFormatter(submission.createdOn)}
                  </Typography>
                </Grid>
              </Fragment>
            );
          })
        ) : (
          <Grid item xs={10} sx={styles.rowStyles}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ color: colors.text.secondary, marginBottom: '4px', marginTop: '4px' }}
            >
              No submissions
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default SubmissionList;
